import { Component } from '@theme/component';
import { scrollTo, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';
import { trapFocus, removeTrapFocus } from '@theme/focus';

class SimucubeConfiguratorPanel extends Component {
  requiredRefs = ['edgeTab', 'tabs', 'scroller'];

  #abortController = new AbortController();

  #spy;

  #tabsResize;

  #header;

  #root;

  #headerFrame;

  #headerOffset = 0;

  #scrollLock;

  #lockedScroll = false;

  #viewReturnFocus = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.refs.edgeTab.addEventListener('click', this.#onEdgeTabClick, { signal });
    this.refs.tabs.addEventListener('click', this.#onTabClick, { signal });
    this.addEventListener('click', this.#onViewClick, { signal });
    this.addEventListener('keydown', this.#onViewKeydown, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#onBreakpointChange, { signal });

    this.#observeSteps();
    this.#watchHeader();
    this.#syncEdgeTab();
    this.#syncScrollerPrevent();
    this.#watchTabsSize();

    requestAnimationFrame(() => this.classList.add('is-animatable'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#spy?.disconnect();
    this.#tabsResize?.disconnect();
    cancelAnimationFrame(this.#headerFrame);
    this.#releaseScroll();
    removeTrapFocus();
  }

  #watchTabsSize() {
    this.#tabsResize = new ResizeObserver(this.#updateProgress);
    this.#tabsResize.observe(this.refs.tabs);
  }

  #watchHeader() {
    this.#header = document.querySelector('header-component[sticky]');
    this.#root = this.closest('.sc-cfg');
    if (!this.#header || !(this.#root instanceof HTMLElement)) return;

    this.#syncHeaderTracking();
  }

  #syncHeaderTracking() {
    cancelAnimationFrame(this.#headerFrame);
    if (this.#isMobile && this.#header) this.#trackHeader();
  }

  #trackHeader = () => {
    this.#headerFrame = requestAnimationFrame(this.#trackHeader);

    const offset = Math.max(0, this.#header.getBoundingClientRect().bottom);
    if (Math.abs(offset - this.#headerOffset) < 0.5) return;

    this.#headerOffset = offset;
    this.#root.style.setProperty('--sc-cfg-header-offset', `${offset}px`);
  };

  get #isMobile() {
    return !scrollContainerMediaQuery.matches;
  }

  #onBreakpointChange = () => {
    if (!this.#isMobile) {
      this.#releaseScroll();
      this.open();
    }

    this.#spy?.disconnect();
    this.#observeSteps();
    this.#syncHeaderTracking();
    this.#syncScrollerPrevent();
  };

  #syncScrollerPrevent() {
    this.refs.scroller.toggleAttribute('data-lenis-prevent', !this.#isMobile);
  }

  #lockScroll() {
    if (this.#lockedScroll || !this.#isMobile) return;
    this.#lockedScroll = true;
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #releaseScroll() {
    if (!this.#lockedScroll) return;
    this.#lockedScroll = false;
    document.documentElement.removeAttribute('scroll-lock');
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;
    this.toggleAttribute('open', true);
    this.#syncEdgeTab();
  }

  close() {
    if (!this.isOpen) return;
    this.toggleAttribute('open', false);
    this.#syncEdgeTab();
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  #onEdgeTabClick = () => {
    this.toggle();
    if (this.isOpen && this.#isMobile) this.#scrollToConfigurator();
  };

  #scrollToConfigurator() {
    const root = this.closest('.sc-cfg');
    if (!(root instanceof HTMLElement)) return;

    const top = root.getBoundingClientRect().top;
    if (top >= 0) return;

    const headerHeight = this.#header instanceof HTMLElement ? this.#header.offsetHeight : 0;
    scrollTo({ top: top + getScrollTop() - headerHeight, behavior: 'smooth' });
  }

  showCompare(handle) {
    const view = this.querySelector(`[data-compare-for="${CSS.escape(handle)}"]`);
    if (!(view instanceof HTMLElement)) return;

    this.#hideViews();
    view.hidden = false;
    this.dataset.view = 'compare';
    this.open();
    this.#lockScroll();
    this.#enterView(view);
  }

  showSummary(groups, emptyText) {
    this.renderSummary(groups, emptyText);

    const { summaryView } = this.refs;
    if (!summaryView) return;

    this.#hideViews();
    summaryView.hidden = false;
    this.dataset.view = 'summary';
    this.open();
    this.#lockScroll();
    this.#enterView(summaryView);
  }

  renderSummary(groups, emptyText) {
    const { summaryBody, summaryGroupTemplate, summaryRowTemplate } = this.refs;
    if (!summaryBody || !summaryGroupTemplate || !summaryRowTemplate) return;

    summaryBody.replaceChildren();

    if (!groups.length) {
      const empty = document.createElement('p');
      empty.className = 'sc-cfg-summary__empty';
      empty.textContent = emptyText;
      summaryBody.append(empty);
      return;
    }

    for (const group of groups) {
      const groupNode = summaryGroupTemplate.content.cloneNode(true);
      const title = groupNode.querySelector('[data-group-title]');
      const rows = groupNode.querySelector('[data-group-rows]');
      if (title) title.textContent = group.title;

      for (const row of group.rows) {
        const rowNode = summaryRowTemplate.content.cloneNode(true);
        const thumb = rowNode.querySelector('[data-row-thumb]');
        const name = rowNode.querySelector('[data-row-name]');
        const note = rowNode.querySelector('[data-row-note]');
        const price = rowNode.querySelector('[data-row-price]');

        if (thumb instanceof HTMLImageElement) {
          if (row.thumbnail) thumb.src = row.thumbnail;
          else thumb.remove();
        }
        if (name) name.textContent = row.name;
        if (note instanceof HTMLElement) {
          note.hidden = !row.note;
          note.textContent = row.note ?? '';
        }
        if (price instanceof HTMLElement) {
          price.hidden = !row.price;
          price.textContent = row.price ?? '';
        }

        rows?.append(rowNode);
      }

      summaryBody.append(groupNode);
    }
  }

  showSteps() {
    const wasInView = this.dataset.view === 'compare' || this.dataset.view === 'summary';

    this.#hideViews();
    this.dataset.view = 'steps';
    this.#releaseScroll();

    if (wasInView) this.#exitView();
  }

  #enterView(view) {
    this.#viewReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const modal = this.#lockedScroll;
    if (modal) view.setAttribute('aria-modal', 'true');
    else view.removeAttribute('aria-modal');

    const target = view.querySelector('[data-close-view]');
    if (target instanceof HTMLElement) target.focus();

    if (modal) trapFocus(view);
  }

  #exitView() {
    removeTrapFocus();

    for (const view of this.querySelectorAll('.sc-cfg-view[aria-modal]')) {
      view.removeAttribute('aria-modal');
    }

    const target = this.#viewReturnFocus;
    this.#viewReturnFocus = null;
    if (target?.isConnected) target.focus();
  }

  #onViewKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.dataset.view !== 'compare' && this.dataset.view !== 'summary') return;

    event.stopPropagation();
    this.showSteps();
  };

  setActiveTab(handle) {
    let active;

    for (const tab of this.refs.tabs.querySelectorAll('[data-tab]')) {
      if (!(tab instanceof HTMLElement)) continue;
      const isActive = tab.dataset.tab === handle;
      tab.dataset.active = String(isActive);
      tab.setAttribute('aria-current', String(isActive));
      if (isActive) active = tab;
    }

    if (active) this.#revealTab(active);
    this.#updateProgress();
  }

  #updateProgress = () => {
    const nav = this.refs.tabs;
    const tabs = nav.querySelectorAll('[data-tab]');
    if (!tabs.length) return;

    const active = nav.querySelector('[data-tab][data-active="true"]');
    const singleRow = tabs[0].offsetTop === tabs[tabs.length - 1].offsetTop;
    const end = singleRow && active instanceof HTMLElement ? active.offsetLeft + active.offsetWidth : 0;

    nav.style.setProperty('--sc-cfg-progress', `${end}px`);
    nav.toggleAttribute('data-tabs-wrapped', !singleRow);
  };

  #revealTab(tab) {
    const nav = this.refs.tabs;
    if (nav.scrollWidth <= nav.clientWidth) return;

    const gutter = 16;
    const navBox = nav.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();

    let delta = 0;
    if (tabBox.left < navBox.left + gutter) delta = tabBox.left - navBox.left - gutter;
    else if (tabBox.right > navBox.right - gutter) delta = tabBox.right - navBox.right + gutter;
    if (!delta) return;

    nav.scrollTo({ left: nav.scrollLeft + delta, behavior: 'smooth' });
  }

  setTabComplete(handle, complete) {
    const tab = this.refs.tabs.querySelector(`[data-tab="${CSS.escape(handle)}"]`);
    if (tab instanceof HTMLElement) tab.dataset.complete = String(complete);
  }

  #hideViews() {
    for (const view of this.querySelectorAll('.sc-cfg-view')) {
      if (view instanceof HTMLElement) view.hidden = true;
    }
  }

  #syncEdgeTab() {
    const { edgeTab } = this.refs;
    const label = edgeTab.querySelector('.sc-cfg__edge-label');
    if (!(label instanceof HTMLElement)) return;

    const openLabel = label.dataset.openLabel ?? '';
    const closeLabel = label.dataset.closeLabel ?? label.textContent?.trim() ?? '';
    label.dataset.closeLabel = closeLabel;
    label.textContent = this.isOpen ? closeLabel : openLabel;
    edgeTab.setAttribute('aria-expanded', String(this.isOpen));

    this.closest('.sc-cfg')?.setAttribute('data-panel-open', String(this.isOpen));
  }

  #onTabClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-tab]') : null;
    if (!tab?.dataset.tab) return;

    const step = this.refs.scroller.querySelector(`.sc-cfg-step[data-step="${CSS.escape(tab.dataset.tab)}"]`);
    if (!(step instanceof HTMLElement)) return;

    this.showSteps();
    this.setActiveTab(tab.dataset.tab);

    window.clearTimeout(this.#scrollLock);
    this.#scrollLock = window.setTimeout(() => {
      this.#scrollLock = undefined;
    }, 700);

    if (this.#isMobile) {
      const stickyTop = parseFloat(getComputedStyle(this.refs.tabs).top);
      const offset = this.refs.tabs.offsetHeight + (Number.isFinite(stickyTop) ? stickyTop : 0);
      scrollTo({ top: step.getBoundingClientRect().top + getScrollTop() - offset, behavior: 'smooth' });
      return;
    }

    this.refs.scroller.scrollTo({ top: step.offsetTop, behavior: 'smooth' });
  };

  #onViewClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-close-view]')) this.showSteps();
  };

  #observeSteps() {
    const steps = this.refs.scroller.querySelectorAll('.sc-cfg-step');
    if (!steps.length) return;

    this.#spy = new IntersectionObserver(
      (entries) => {
        if (this.#scrollLock) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        const handle = visible?.target instanceof HTMLElement ? visible.target.dataset.step : undefined;
        if (!handle) return;

        this.setActiveTab(handle);
        this.dispatchEvent(new CustomEvent('sc-configurator:step', { bubbles: true, detail: { handle } }));
      },
      this.#isMobile
        ? { root: null, rootMargin: '-20% 0px -70% 0px', threshold: 0 }
        : { root: this.refs.scroller, rootMargin: '-10% 0px -70% 0px', threshold: 0 },
    );

    for (const step of steps) this.#spy.observe(step);
  }
}

if (!customElements.get('sc-config-panel')) {
  customElements.define('sc-config-panel', SimucubeConfiguratorPanel);
}
